<script setup lang="ts">
import { computed } from 'vue'
import { clamp } from 'lodash'
import { formatCurrency } from '@/modules/shared/utils/currency'
import {
  VCard,
  VCardTitle,
  VDescriptionListDetailed,
  VMetricCard,
  VOperatingActivity,
} from '@/modules/shared/components'
import type { DescriptionListDetailedItem, OperatingActivityItem } from '@/modules/shared/components/types'
import { ArrowDownToLine, Banknote, Percent, TrendingUp } from 'lucide-vue-next'
import { useHiddenMetrics } from '@/modules/workspace/composables/use-hidden-metrics'
import SummaryCardSkeleton from './summary-card-skeleton.vue'

const props = withDefaults(
  defineProps<{
    calledPaid: number
    calledPending: number
    totalCalled: number
    totalCommitted: number
    managementFees: number
    otherFees: number
    fundsDeployed: number
    cashPosition: number
    operatingIncome: number
    operatingExpenses: number
    interestPaid: number
    interestReceived: number
    currency?: string
    skeleton?: boolean
  }>(),
  { currency: 'USD', skeleton: false },
)

const { hidden } = useHiddenMetrics()

const fmt = (value: number) => formatCurrency(value, { currency: props.currency, decimals: 0 })

const metricValue = computed(() => fmt(props.totalCalled))

const paidBar = computed(() => {
  if (props.calledPaid <= 0) return undefined
  const width = props.totalCommitted > 0 ? clamp((props.calledPaid / props.totalCommitted) * 100, 0, 100) : 0
  return { value: fmt(props.calledPaid), width, color: '#3092b3', gradientTo: '#246586' }
})

const pendingBar = computed(() => {
  if (props.calledPending <= 0) return undefined
  const width =
    props.totalCommitted > 0
      ? clamp(((props.calledPaid + props.calledPending) / props.totalCommitted) * 100, 0, 100)
      : 0
  return { value: fmt(props.calledPending), width, color: '#91c4d9' }
})

const totalBar = computed(() => ({
  value: fmt(props.totalCommitted),
  label: 'Committed',
}))

const rows = computed<DescriptionListDetailedItem[]>(() => {
  const items: DescriptionListDetailedItem[] = [
    {
      icon: ArrowDownToLine,
      label: 'Capital Paid',
      sublabel: 'received from investors',
      value: fmt(props.calledPaid),
      color: '#3092b3',
    },
    {
      icon: Percent,
      label: 'Total Fees',
      sublabel: 'management + other fees',
      value: fmt(props.managementFees + props.otherFees),
      color: '#7d8ad0',
    },
    {
      icon: TrendingUp,
      label: 'Funds Deployed',
      sublabel: 'net capital deployed into investments',
      value: fmt(props.fundsDeployed),
      color: '#246586',
      isHidden: hidden.value.initialValue,
    },
    {
      icon: Banknote,
      label: 'Cash Position',
      sublabel: 'available cash balance',
      value: fmt(props.cashPosition),
      color: '#059669',
      isHidden: hidden.value.cashPosition,
    },
  ]
  return items.filter((row) => !row.isHidden)
})

const operatingActivityItems = computed<OperatingActivityItem[]>(() => [
  { label: 'Operating Income', value: fmt(props.operatingIncome), color: '#a78bfa' },
  { label: 'Operating Expenses', value: fmt(props.operatingExpenses), color: '#c4b5fd' },
  { label: 'Interest Paid', value: fmt(props.interestPaid), color: '#8b5cf6' },
  { label: 'Interest Allocated', value: fmt(props.interestReceived), color: '#ddd6fe' },
])
</script>

<template>
  <VCard>
    <VCardTitle title="Capital Summary" />

    <SummaryCardSkeleton v-if="skeleton" />

    <template v-else>
      <div class="flex items-end justify-between">
        <div>
          <div class="text-3xl font-medium tracking-tight text-[#246586]">
            {{ metricValue }}
          </div>
          <div class="mt-1 text-sm font-medium text-gray-700">Called</div>
        </div>
        <div>
          <div class="text-right text-2xl font-medium tracking-tight text-gray-600">
            {{ fmt(props.totalCommitted) }}
          </div>
          <div class="mt-1 text-right text-sm font-medium text-gray-700">Committed</div>
        </div>
      </div>

      <div class="mb-6">
        <VMetricCard
          label="Called"
          :value="metricValue"
          :paid="paidBar"
          :pending="pendingBar"
          :committedValue="fmt(props.totalCommitted)"
        />
      </div>
      <VDescriptionListDetailed :items="rows" />
      <div class="border-t border-[var(--divider-color,#EEEFF0)]"></div>
      <VOperatingActivity :items="operatingActivityItems" />
    </template>
  </VCard>
</template>
